import React, { createContext, useContext, useState, useEffect } from 'react';

interface HelpContextType {
  content: React.ReactNode | null;
  setContent: (content: React.ReactNode | null) => void;
  setContentOnly: (content: React.ReactNode | null) => void;
  isToolsOpen: boolean;
  setIsToolsOpen: (isOpen: boolean) => void;
}

const HelpContext = createContext<HelpContextType | null>(null);

export const HelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize isToolsOpen from localStorage or default to false
  const [isToolsOpen, setIsToolsOpen] = useState<boolean>(() => {
    const savedState = localStorage.getItem('helpPanelOpen');
    return savedState === 'true';
  });
  const [content, setContent] = useState<React.ReactNode | null>(null);
  
  // Update localStorage when isToolsOpen changes
  useEffect(() => {
    localStorage.setItem('helpPanelOpen', isToolsOpen.toString());
  }, [isToolsOpen]);
  
  // Set content without affecting the panel state
  const setContentOnly = (newContent: React.ReactNode | null) => {
    setContent(newContent);
  };
  
  // Set content and open the panel if content is provided
  const setHelpContent = (newContent: React.ReactNode | null) => {
    setContent(newContent);
    if (newContent) {
      setIsToolsOpen(true);
    }
  };
  
  return (
    <HelpContext.Provider value={{ 
      content, 
      setContent: setHelpContent,
      setContentOnly,
      isToolsOpen, 
      setIsToolsOpen 
    }}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelpPanel = () => {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error('useHelpPanel must be used within a HelpProvider');
  }
  
  return context;
};
